import React, { useState, useEffect, useRef, useMemo } from "react";
import { FormControlLabel, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { LoadingSpinner } from "ctech-react-components";
import {
  createProvider,
  updateProvider,
} from "../../../../services/paymentApi";
import Billing from "../../../../services/billingApi";
import { getErrorMessage } from "../../utils/errorHandler";
import type { LocalPaymentData } from "../../helpers";
import type { PaymentProviderCatalogDto } from "types/paymentProviderCatalog";
import type {
  PaymentMethodsProps,
  DealerMethodsPaymentsProps,
} from "store/modules/DealerCare/types.store";
import * as Styled from "./styles";
import MastercardFlag from "assets/flags/mastercard.svg";
import VisaFlag from "assets/flags/visa.svg";
import HipercardFlag from "assets/flags/hipercard.svg";
import CaixaFlag from "assets/flags/caixa.svg";
import EloFlag from "assets/flags/elo.svg";
import AmexFlag from "assets/flags/amex.svg";
import BanrisulFlag from "assets/flags/banrisul.png";
import {
  PaymentMethod,
  MethodId,
  ProviderState,
  ProviderType,
  ProviderCode,
} from "../../enums";
import { PAYMENT_METHOD_ORDER } from "../../constants/paymentMethods";
import { resolvePaymentMethodsForBillingSave } from "../../utils/resolvePaymentMethodsForBillingSave";
import {
  parseLocalPaymentProviderSchema,
  buildPaymentMethodsFromSchema,
  type PaymentMethodType,
} from "../../utils/localPaymentFromSchema";

export interface DealerPaymentDeliveryProps {
  localPaymentData?: LocalPaymentData | null;
  dealerId?: string;
  paymentMethodsFromApi?: PaymentMethodsProps[] | null;
  onSaveSuccess?: (updatedPaymentMethods?: PaymentMethodsProps[]) => void;
  availableProviders?: PaymentProviderCatalogDto[] | null;
}

const METHOD_ID_TO_API_VALUE: Record<string, PaymentMethod> = {
  [MethodId.Credit]: PaymentMethod.Credit,
  [MethodId.Debit]: PaymentMethod.Debit,
  [MethodId.Pix]: PaymentMethod.Pix,
  [MethodId.Dinheiro]: PaymentMethod.Cash,
};

function buildOnDeliveryPayload(
  onDeliveryType: PaymentMethodsProps,
  formMethods: PaymentMethodType[],
): PaymentMethodsProps {
  const updatedMethods: DealerMethodsPaymentsProps[] =
    onDeliveryType.methods.map((apiMethod) => {
      const formMethod = formMethods.find(
        (fm) =>
          fm.description.toLowerCase() === apiMethod.description.toLowerCase(),
      );
      const enabled = formMethod ? formMethod.enabled : apiMethod.enabled;
      const isCardMethod =
        apiMethod.asksFor === "Brand" && apiMethod.brands?.length;
      let { brands } = apiMethod;
      if (isCardMethod && formMethod?.brands?.length) {
        brands = apiMethod.brands!.map((apiBrand) => {
          const formBrand = formMethod.brands?.find(
            (fb) =>
              fb.description?.toLowerCase() ===
              apiBrand.description?.toLowerCase(),
          );
          return {
            ...apiBrand,
            enabled: formBrand ? formBrand.enabled : apiBrand.enabled,
          };
        });
      }
      return {
        id: apiMethod.id,
        order: apiMethod.order,
        description: apiMethod.description,
        asksFor: apiMethod.asksFor,
        enabled,
        brands: Array.isArray(brands) ? brands : [],
      };
    });
  return {
    id: onDeliveryType.id,
    order: onDeliveryType.order,
    description: onDeliveryType.description,
    methods: updatedMethods,
  };
}

const LOCAL_PAYMENT_PROVIDER_CONFIG = {
  name: "Pagamento na entrega",
  code: ProviderCode.LocalPayment,
  priority: 0,
  state: ProviderState.Enabled,
  type: ProviderType.LocalPayment,
};

const DealerPaymentDelivery: React.FC<DealerPaymentDeliveryProps> = ({
  localPaymentData,
  dealerId,
  paymentMethodsFromApi,
  onSaveSuccess,
  availableProviders,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [methods, setMethods] = useState<PaymentMethodType[]>([]);
  const methodsRef = useRef<PaymentMethodType[]>([]);
  const [localPaymentProviderCatalog, setLocalPaymentProviderCatalog] =
    useState<PaymentProviderCatalogDto | null>(null);

  const schemaDerived = useMemo(
    () => parseLocalPaymentProviderSchema(localPaymentProviderCatalog?.schema),
    [localPaymentProviderCatalog?.schema],
  );

  useEffect(() => {
    const initialMethods = buildPaymentMethodsFromSchema(
      localPaymentData || null,
      schemaDerived,
    );
    methodsRef.current = initialMethods;
    setMethods(initialMethods);
  }, [localPaymentData, schemaDerived]);

  useEffect(() => {
    const match = availableProviders?.find(
      (item) => item.code === ProviderCode.LocalPayment,
    );
    setLocalPaymentProviderCatalog(match ?? null);
  }, [availableProviders]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setMethods([...methodsRef.current]);
  };

  const handleMethodToggle = (methodId: string) => {
    setMethods((prev) =>
      prev.map((method) => {
        if (method.id === methodId) {
          const newEnabled = !method.enabled;
          return {
            ...method,
            enabled: newEnabled,
            brands: method.brands?.map((brand) => ({
              ...brand,
              enabled: newEnabled ? brand.enabled : false,
            })),
          };
        }
        return method;
      }),
    );
  };

  const handleBrandToggle = (methodId: string, brandId: string) => {
    setMethods((prev) =>
      prev.map((method) =>
        method.id === methodId
          ? {
              ...method,
              brands: method.brands?.map((brand) =>
                brand.id === brandId
                  ? { ...brand, enabled: !brand.enabled }
                  : brand,
              ),
            }
          : method,
      ),
    );
  };

  const getEnabledBrands = (methodId: string): string[] => {
    const method = methods.find((m) => m.id === methodId);
    return (
      method?.brands
        ?.filter((brand) => brand.enabled)
        .map((brand) => brand.description) || []
    );
  };

  const validatePaymentMethods = (): string | null => {
    const creditCardMethod = methods.find(
      (method) => method.id === MethodId.Credit,
    );
    const debitCardMethod = methods.find(
      (method) => method.id === MethodId.Debit,
    );

    if (creditCardMethod?.enabled) {
      const hasCreditCardBrand = creditCardMethod.brands?.some(
        (brand) => brand.enabled,
      );
      if (!hasCreditCardBrand) {
        return "Cartão de Crédito está selecionado. Selecione pelo menos uma bandeira.";
      }
    }

    if (debitCardMethod?.enabled) {
      const hasDebitCardBrand = debitCardMethod.brands?.some(
        (brand) => brand.enabled,
      );
      if (!hasDebitCardBrand) {
        return "Cartão de Débito está selecionado. Selecione pelo menos uma bandeira.";
      }
    }

    return null;
  };

  const handleSave = async () => {
    if (!dealerId) {
      toast("ID do revendedor não encontrado", { type: "error" });
      return;
    }

    const validationError = validatePaymentMethods();
    if (validationError) {
      toast(
        validationError ||
          "É necessário preencher os campos para ativação do pagamento na entrega.",
        { type: "error" },
      );
      return;
    }

    setIsLoading(true);

    try {
      const billingList = await resolvePaymentMethodsForBillingSave(
        dealerId,
        paymentMethodsFromApi,
      );
      if (!billingList) {
        toast(
          "Não foi possível sincronizar com o faturamento. Verifique se existem os blocos ONDELIVERY e INAPP.",
          { type: "error" },
        );
        return;
      }

      const enabledMethods = methods
        .filter((method) => method.enabled)
        .map((method) => METHOD_ID_TO_API_VALUE[method.id] || method.id);

      const creditBrands = getEnabledBrands(MethodId.Credit);
      const debitBrands = getEnabledBrands(MethodId.Debit);

      const payload = {
        ...LOCAL_PAYMENT_PROVIDER_CONFIG,
        methods: enabledMethods,
        acquirer: {
          creditBrands: enabledMethods.includes(PaymentMethod.Credit)
            ? creditBrands
            : [],
          debitBrands: enabledMethods.includes(PaymentMethod.Debit)
            ? debitBrands
            : [],
        },
      };

      let providerIdAfterSave = localPaymentData?.id;
      if (!localPaymentData?.id) {
        const createRes = await createProvider({
          merchantId: dealerId,
          data: payload,
        });
        const body = createRes.data as {
          id?: string;
          data?: { id?: string };
        };
        providerIdAfterSave = body?.id ?? body?.data?.id ?? providerIdAfterSave;
      } else {
        await updateProvider({
          merchantId: dealerId,
          providerId: localPaymentData.id,
          data: payload,
        });
      }

      const onDeliveryType = billingList.find(
        (pm) =>
          pm.order === PAYMENT_METHOD_ORDER.DELIVERY ||
          pm.description === "ONDELIVERY",
      );
      const inappType = billingList.find(
        (pm) =>
          pm.order === PAYMENT_METHOD_ORDER.ONLINE ||
          pm.description === "INAPP",
      );
      if (!onDeliveryType || !inappType) {
        toast(
          "Não foi possível localizar ONDELIVERY e INAPP para sincronizar o faturamento.",
          { type: "error" },
        );
        return;
      }

      const onDeliveryPayload = buildOnDeliveryPayload(onDeliveryType, methods);
      await Billing.post(`/v2/dealers/${dealerId}/paymentmethods`, [
        onDeliveryPayload,
        inappType,
      ]);

      toast("Alterações aplicadas com sucesso!", {
        type: "success",
      });

      const updatedLocalPaymentData: LocalPaymentData = {
        id: providerIdAfterSave ?? localPaymentData?.id,
        methods: enabledMethods,
        creditBrands: enabledMethods.includes(PaymentMethod.Credit)
          ? creditBrands
          : [],
        debitBrands: enabledMethods.includes(PaymentMethod.Debit)
          ? debitBrands
          : [],
      };

      const updatedMethods = buildPaymentMethodsFromSchema(
        updatedLocalPaymentData,
        schemaDerived,
      );
      methodsRef.current = updatedMethods;
      setMethods(updatedMethods);
      setIsEditing(false);
      onSaveSuccess?.([onDeliveryPayload, inappType]);
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(
        error,
        "Houve um erro ao salvar as alterações. Tente novamente!",
      );
      toast(errorMessage, { type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const brandIconMap: Record<string, string> = {
    mastercard: MastercardFlag,
    visa: VisaFlag,
    hipercard: HipercardFlag,
    caixa: CaixaFlag,
    banrisul: BanrisulFlag,
    elo: EloFlag,
    amex: AmexFlag,
  };

  const getBrandIcon = (brandName: string) => {
    const normalizedName = brandName.toLowerCase();
    const iconSrc = brandIconMap[normalizedName];

    if (!iconSrc) return null;

    return (
      <Styled.BrandIconImage key={brandName} src={iconSrc} alt={brandName} />
    );
  };

  const isActivePaymentOnDelivery = methods?.some((method) => method.enabled);
  return (
    <Styled.Container>
      <LoadingSpinner isSpinnerVisible={isLoading} />
      <Styled.Header>
        <Styled.HeaderCaption variant="caption">
          {t("paymentOnDelivery")}
        </Styled.HeaderCaption>
        {isActivePaymentOnDelivery ? (
          <Styled.ChipActive
            label={t("paymentOnDeliveryActive")}
            size="small"
          />
        ) : (
          <Styled.ChipInactive
            label={t("paymentOnDeliveryInactive")}
            size="small"
          />
        )}
      </Styled.Header>

      {!isEditing ? (
        <>
          <Styled.PaymentOptions>
            {methods.map((method) => (
              <Styled.PaymentOption key={method.id}>
                <Styled.PaymentOptionContent>
                  <Styled.CheckCircleIconStyled enabled={method.enabled} />
                  <Styled.MethodDescription enabled={method.enabled}>
                    {method.description}
                  </Styled.MethodDescription>
                </Styled.PaymentOptionContent>
                {method.enabled &&
                  method.brands &&
                  method.brands.length > 0 && (
                    <Styled.CardFlags>
                      {method.brands
                        .filter((brand) => brand.enabled)
                        .map((brand) => getBrandIcon(brand.description))}
                    </Styled.CardFlags>
                  )}
              </Styled.PaymentOption>
            ))}
          </Styled.PaymentOptions>

          <Divider />

          <Styled.ActionsContainer>
            <Styled.EditButton variant="contained" onClick={handleEditClick}>
              {t("edit")}
            </Styled.EditButton>
          </Styled.ActionsContainer>
        </>
      ) : (
        <>
          <Styled.MethodsContainer>
            {methods.map((method, index) => (
              <React.Fragment key={method.id}>
                <Styled.MethodContainer>
                  <FormControlLabel
                    control={
                      <Styled.CheckboxPrimary
                        checked={method.enabled}
                        onChange={() => handleMethodToggle(method.id)}
                      />
                    }
                    label={
                      <Styled.MethodLabel>
                        {method.description}
                      </Styled.MethodLabel>
                    }
                  />

                  {method.enabled &&
                    method.brands &&
                    method.brands.length > 0 && (
                      <Styled.BrandsContainer>
                        <Styled.BrandsTitle variant="caption">
                          Bandeiras Aceitas
                        </Styled.BrandsTitle>
                        <Styled.BrandsList>
                          {method.brands.map((brand) => (
                            <FormControlLabel
                              key={brand.id}
                              control={
                                <Styled.CheckboxPrimarySmall
                                  checked={brand.enabled}
                                  onChange={() =>
                                    handleBrandToggle(method.id, brand.id)
                                  }
                                  size="small"
                                />
                              }
                              label={
                                <Styled.BrandLabel>
                                  {brand.description}
                                </Styled.BrandLabel>
                              }
                            />
                          ))}
                        </Styled.BrandsList>
                      </Styled.BrandsContainer>
                    )}
                </Styled.MethodContainer>
                {index < methods.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </Styled.MethodsContainer>

          <Divider />

          <Styled.ActionsContainer>
            <Styled.CancelButton variant="outlined" onClick={handleCancelEdit}>
              Cancelar
            </Styled.CancelButton>
            <Styled.ApplyButton variant="contained" onClick={handleSave}>
              Aplicar
            </Styled.ApplyButton>
          </Styled.ActionsContainer>
        </>
      )}
    </Styled.Container>
  );
};

export default DealerPaymentDelivery;
